/* Floretti 3.0 — interactie in de productgids.

   Twee custom elements, zodat de theme-editor ze opnieuw initialiseert
   zodra een sectie herladen wordt:

   1. floretti-dose-meter — het gekozen wasje stuurt de trommelvulling,
      het aantal strips, de uitgelezen dosering en de hintregel.
   2. floretti-scent-picker — tabs die het geurpaneel wisselen en de
      accentkleur van het podium meenemen.

   Zonder JS blijft alles leesbaar: de radio's staan op "normale was" en
   de drie geurpanelen staan gewoon onder elkaar. Beide elementen zetten
   daarom pas een is-enhanced-klasse zodra ze daadwerkelijk draaien. */
(function () {
  'use strict';

  /* ============================================================
     Doseermeter
     ============================================================ */

  class FlorettiDoseMeter extends HTMLElement {
    connectedCallback() {
      this.inputs = Array.prototype.slice.call(this.querySelectorAll('input[type="radio"]'));
      if (!this.inputs.length) return;

      this.strips = this.querySelector('[data-strips]');
      this.doseOut = this.querySelector('[data-dose-value]');
      this.hintOut = this.querySelector('[data-hint-out]');

      this._onChange = this.handleChange.bind(this);
      this.inputs.forEach(function (input) {
        input.addEventListener('change', this._onChange);
      }, this);

      this.classList.add('is-enhanced');
      this.apply(this.currentInput(), true);
      this.watchViewport();
    }

    disconnectedCallback() {
      if (this._onChange) {
        this.inputs.forEach(function (input) {
          input.removeEventListener('change', this._onChange);
        }, this);
      }
      if (this._observer) this._observer.disconnect();
    }

    currentInput() {
      for (var i = 0; i < this.inputs.length; i++) {
        if (this.inputs[i].checked) return this.inputs[i];
      }
      return this.inputs[0];
    }

    handleChange(event) {
      this.apply(event.target, false);
    }

    /* silent = eerste keer: de hintregel is dan al server-side gerenderd,
       dus we hoeven aria-live niet meteen te laten praten. */
    apply(input, silent) {
      if (!input) return;

      var fill = input.getAttribute('data-fill');
      if (fill) this.style.setProperty('--fill', fill);

      if (this.strips) {
        this.strips.setAttribute('data-dose', input.getAttribute('data-dose') || '1');
      }

      if (this.doseOut) {
        this.doseOut.textContent = input.getAttribute('data-dose-label') || '';
      }

      if (this.hintOut && !silent) {
        this.hintOut.textContent = input.getAttribute('data-hint') || '';
      }
    }

    /* De trommel loopt pas vol zodra de meter in beeld komt. Staat hij er al
       gevuld als je aankomt, dan mis je juist de beweging die uitlegt wat
       het ding doet. */
    watchViewport() {
      if (!('IntersectionObserver' in window)) {
        this.classList.add('is-inview');
        return;
      }

      var element = this;
      this._observer = new IntersectionObserver(
        function (entries, observer) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            element.classList.add('is-inview');
            observer.disconnect();
          });
        },
        { threshold: 0.25 }
      );
      this._observer.observe(this);
    }
  }

  /* ============================================================
     Geurkiezer
     ============================================================ */

  class FlorettiScentPicker extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.prototype.slice.call(this.querySelectorAll('[role="tab"]'));
      this.stage = this.querySelector('.pg-scents__stage');
      if (this.tabs.length < 2 || !this.stage) return;

      this.panels = this.tabs.map(function (tab) {
        return document.getElementById(tab.getAttribute('aria-controls'));
      });
      if (this.panels.indexOf(null) !== -1) return;

      this._onClick = this.handleClick.bind(this);
      this._onKeydown = this.handleKeydown.bind(this);
      this.tabs.forEach(function (tab) {
        tab.addEventListener('click', this._onClick);
        tab.addEventListener('keydown', this._onKeydown);
      }, this);

      this.classList.add('is-enhanced');
      this.select(0, false);
    }

    disconnectedCallback() {
      if (!this._onClick) return;
      this.tabs.forEach(function (tab) {
        tab.removeEventListener('click', this._onClick);
        tab.removeEventListener('keydown', this._onKeydown);
      }, this);
    }

    handleClick(event) {
      this.select(this.tabs.indexOf(event.currentTarget), false);
    }

    /* Pijltjestoetsen tussen de tabs, zoals de tab-pattern voorschrijft:
       Tab springt de hele groep in of uit, pijltjes wisselen erbinnen. */
    handleKeydown(event) {
      var current = this.tabs.indexOf(event.currentTarget);
      var next = null;

      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        next = (current + 1) % this.tabs.length;
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        next = (current - 1 + this.tabs.length) % this.tabs.length;
      } else if (event.key === 'Home') {
        next = 0;
      } else if (event.key === 'End') {
        next = this.tabs.length - 1;
      }

      if (next === null) return;
      event.preventDefault();
      this.select(next, true);
    }

    select(index, focusTab) {
      this.tabs.forEach(function (tab, i) {
        var active = i === index;
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        /* Roving tabindex: alleen de actieve tab zit in de taborde. */
        tab.tabIndex = active ? 0 : -1;
        if (active && focusTab) tab.focus();
      });

      this.panels.forEach(function (panel, i) {
        if (i === index) {
          panel.removeAttribute('hidden');
        } else {
          panel.setAttribute('hidden', '');
        }
      });

      /* Het podium kleurt mee met de gekozen geur. De accentwaarde staat
         als custom property op het paneel zelf, dus die lezen we terug. */
      var accent = this.panels[index].style.getPropertyValue('--scent-accent');
      if (accent) this.stage.style.setProperty('--stage-accent', accent.trim());
    }
  }

  if (!customElements.get('floretti-dose-meter')) {
    customElements.define('floretti-dose-meter', FlorettiDoseMeter);
  }
  if (!customElements.get('floretti-scent-picker')) {
    customElements.define('floretti-scent-picker', FlorettiScentPicker);
  }
})();
